import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { migrationsApi, migrationKeys } from '@/lib/api';
import { massMigrationKeys } from '@/lib/api/massMigration';
import type { MassMigrationBatchDto, MassMigrationBatchOrgDto } from '@/types/massMigration';
import { StatTile } from './StatTile';
import { OrgStatusBadge } from './OrgStatusBadge';
import { OrgDetailView } from './OrgDetailView';
import { InviteDrainRow, InviteCount } from './InviteDrainRow';

// ─── Helpers ──────────────────────────────────────────────────────────────────

type OrgFilter = 'All' | 'Failed' | 'Partial' | 'Complete';

const ORG_STATUS_LABEL: Record<string, string> = {
  InProgress: 'In Progress',
  Succeeded: 'Succeeded',
  Failed: 'Failed',
  Pending: 'Pending',
  Partial: 'Partial',
  Cancelled: 'Cancelled',
};

function downloadCsv(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  try {
    link.click();
  } finally {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

// ─── CompletionView ───────────────────────────────────────────────────────────

interface CompletionViewProps {
  batch: MassMigrationBatchDto;
}

export function CompletionView({ batch }: CompletionViewProps) {
  const [orgFilter, setOrgFilter] = useState<OrgFilter>('All');
  const [selectedOrg, setSelectedOrg] = useState<MassMigrationBatchOrgDto | null>(null);

  const partialCount = batch.partial;

  const progressColor =
    batch.status === 'Succeeded'
      ? '[&>div]:bg-success'
      : batch.status === 'Partial'
        ? '[&>div]:bg-warning'
        : '[&>div]:bg-destructive';

  const statusBadgeVariant =
    batch.status === 'Succeeded'
      ? 'success'
      : batch.status === 'Partial'
        ? 'warning'
        : batch.status === 'Failed'
          ? 'destructive'
          : 'secondary';

  const statusLabel: Record<string, string> = {
    Succeeded: 'Succeeded',
    Partial: 'Partial',
    Failed: 'Failed',
    Cancelled: 'Cancelled',
  };

  const filteredOrgs: MassMigrationBatchOrgDto[] = batch.orgs.filter((org) => {
    if (orgFilter === 'All') return true;
    if (orgFilter === 'Failed') return org.status === 'Failed';
    if (orgFilter === 'Partial') return org.status === 'Partial';
    if (orgFilter === 'Complete') return org.status === 'Succeeded';
    return true;
  });

  const orgFilters: OrgFilter[] = ['All', 'Complete', 'Partial', 'Failed'];

  function handleExportFailures() {
    const failedOrgs = batch.orgs.filter(
      (o) => o.status === 'Failed' || o.status === 'Partial',
    );
    const rows = [
      ['org_name', 'bridge_id', 'status'].join(','),
      ...failedOrgs.map((o) =>
        [
          JSON.stringify(o.organizationName),
          JSON.stringify(o.bridgeEntityUuid),
          JSON.stringify(ORG_STATUS_LABEL[o.status] ?? o.status),
        ].join(','),
      ),
    ];
    downloadCsv(rows.join('\n'), `${batch.name}-failures.csv`);
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-xl font-semibold">{batch.name}</h2>
        <Badge variant={statusBadgeVariant as 'success' | 'warning' | 'destructive' | 'secondary'}>
          {statusLabel[batch.status] ?? batch.status}
        </Badge>
        {batch.completedAt && (
          <span className="text-sm text-muted-foreground">
            Completed {new Date(batch.completedAt).toLocaleString()}
          </span>
        )}
        <div className="ml-auto">
          {(batch.failed > 0 || partialCount > 0) && (
            <Button variant="outline" size="sm" onClick={handleExportFailures}>
              Export failures
            </Button>
          )}
        </div>
      </div>

      {/* Final progress bar */}
      <div className="rounded-lg border border-border bg-card p-5 space-y-3">
        <div className="flex items-baseline justify-between">
          <span className="text-base font-medium">
            {batch.succeeded} of {batch.totalOrgs} organizations succeeded
          </span>
          <span className="text-sm text-muted-foreground">
            {batch.totalOrgs > 0
              ? Math.round((batch.succeeded / batch.totalOrgs) * 100)
              : 0}
            %
          </span>
        </div>
        <Progress
          value={batch.totalOrgs > 0 ? (batch.succeeded / batch.totalOrgs) * 100 : 100}
          className={cn('h-3', progressColor)}
          aria-label="Batch completion"
        />
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Total Orgs" value={batch.totalOrgs} />
        <StatTile label="Succeeded" value={batch.succeeded} variant="success" />
        <StatTile label="Partial" value={partialCount} variant="warning" />
        <StatTile label="Failed" value={batch.failed} variant="destructive" />
      </div>

      <InviteDrainRow batch={batch} />

      {/* Org table */}
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium">Organizations</span>
          <div className="flex gap-1 ml-auto" role="group" aria-label="Filter organizations">
            {orgFilters.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setOrgFilter(f)}
                className={cn(
                  'rounded-full px-3 py-1 text-xs font-medium transition-colors',
                  orgFilter === f
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80',
                )}
                aria-pressed={orgFilter === f}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-md border border-border overflow-hidden">
          <table className="w-full text-sm">
            <caption className="sr-only">Organizations in this batch</caption>
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">
                  Organization
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">
                  Bridge ID
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">
                  Status
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">
                  Modules
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">
                  Emailed Invites
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredOrgs.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-8 text-center text-sm text-muted-foreground"
                  >
                    No organizations match this filter.
                  </td>
                </tr>
              ) : (
                filteredOrgs.map((org) => {
                  const canRetry =
                    (org.status === 'Failed' || org.status === 'Partial') &&
                    batch.status !== 'Cancelled';
                  return (
                    <tr
                      key={org.migrationId}
                      className="border-b border-border last:border-0 cursor-pointer hover:bg-muted/50 focus-within:bg-muted/50 transition-colors"
                      onClick={() => setSelectedOrg(org)}
                    >
                      <td className="px-4 py-3">
                        {/* Keyboard/AT entry point for the row. The row itself is not a button:
                            a button role would make the nested Retry control presentational. */}
                        <button
                          type="button"
                          className="rounded-sm text-left font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          aria-label={`View details for ${org.organizationName}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedOrg(org);
                          }}
                        >
                          {org.organizationName}
                        </button>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                        {org.bridgeEntityUuid}
                      </td>
                      <td className="px-4 py-3">
                        <OrgStatusBadge status={org.status} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {org.moduleKeys.map((key) => (
                            <span
                              key={key}
                              className="inline-flex items-center rounded border border-border bg-muted px-1.5 py-0.5 text-xs text-muted-foreground"
                            >
                              {key}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm tabular-nums">
                        <InviteCount org={org} />
                      </td>
                      <td className="px-4 py-3">
                        {canRetry && <RetryButton org={org} batchId={batch.id} />}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Org detail dialog */}
      <Dialog open={selectedOrg !== null} onOpenChange={(open) => { if (!open) setSelectedOrg(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="sr-only">Organization details</DialogTitle>
          </DialogHeader>
          {selectedOrg && (
            <OrgDetailView
              org={selectedOrg}
              batchName={batch.name}
              onBackToBatch={() => setSelectedOrg(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── RetryButton ──────────────────────────────────────────────────────────────

interface RetryButtonProps {
  org: MassMigrationBatchOrgDto;
  batchId: string;
}

function RetryButton({ org, batchId }: RetryButtonProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { mutate, isPending } = useMutation({
    mutationFn: () => migrationsApi.retry(org.migrationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: massMigrationKeys.batch(batchId) });
      void queryClient.invalidateQueries({ queryKey: massMigrationKeys.batches() });
      // OrgDetailView caches logs with staleTime: Infinity — drop them so the next open refetches.
      queryClient.removeQueries({ queryKey: migrationKeys.logs(org.migrationId) });
      toast({ title: 'Retry started', description: `Retrying ${org.organizationName}` });
    },
    onError: (error: Error) => {
      toast({ title: 'Retry failed', description: error.message, variant: 'destructive' });
    },
  });

  // The row opens the org modal on click — keep this click from bubbling into it.
  return (
    <Button
      variant="outline"
      size="sm"
      disabled={isPending}
      aria-label={`Retry ${org.organizationName}`}
      onClick={(e) => {
        e.stopPropagation();
        mutate();
      }}
    >
      {isPending ? 'Retrying…' : 'Retry'}
    </Button>
  );
}
